import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { PlexusCanvas } from "@/components/PlexusCanvas";
import { useSite } from "@/context/SiteContext";
import { Kicker, Reveal } from "@/components/Reveal";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Contact = () => {
    const { t } = useSite();
    const [values, setValues] = useState({ name: "", email: "", organisation: "", message: "" });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");

    const set = (k) => (e) => {
        setValues((v) => ({ ...v, [k]: e.target.value }));
        setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const validate = () => {
        const er = {};
        if (!values.name.trim()) er.name = t.contact.errors.name;
        if (!EMAIL_RE.test(values.email.trim())) er.email = t.contact.errors.email;
        if (!values.organisation.trim()) er.organisation = t.contact.errors.organisation;
        if (!values.message.trim()) er.message = t.contact.errors.message;
        return er;
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const er = validate();
        setErrors(er);
        if (Object.keys(er).length > 0) return;
        setStatus("submitting");
        // PRODUCTION: connect the contact API endpoint here
        // (e.g. POST `${process.env.REACT_APP_BACKEND_URL}/api/contact` with `values`).
        // Simulated submission for the first-release demonstration build.
        window.setTimeout(() => {
            setStatus("success");
            setValues({ name: "", email: "", organisation: "", message: "" });
        }, 1200);
    };

    const fieldClass = (k) =>
        `w-full border bg-white/5 px-4 py-3.5 font-body text-base text-white placeholder:text-white/40 transition-colors duration-200 focus:border-chitta-sky focus:outline-none ${
            errors[k] ? "border-red-400" : "border-white/20"
        }`;

    return (
        <section id="contact" data-testid="contact-section" className="mesh-blue relative overflow-hidden text-white">
            <PlexusCanvas className="pointer-events-none absolute inset-0 h-full w-full opacity-30" />
            <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-14 px-5 py-24 sm:px-8 sm:py-32 lg:grid-cols-12 lg:gap-12 lg:px-12 lg:py-40">
                <div className="lg:col-span-5">
                    <Reveal>
                        <Kicker light>{t.contact.kicker}</Kicker>
                        <h2 className="headline-section mt-6 max-w-[12ch] font-display font-semibold" data-testid="contact-heading">
                            {t.contact.heading}
                        </h2>
                        <p className="mt-10 font-display text-sm font-semibold lowercase tracking-[0.18em] text-chitta-sky">
                            {t.contact.formHeading}
                        </p>
                    </Reveal>
                </div>

                <div className="lg:col-span-7">
                    <Reveal delay={0.12}>
                        {status === "success" ? (
                            <div
                                role="status"
                                data-testid="contact-success"
                                className="flex min-h-[320px] flex-col items-start justify-center border border-white/20 p-10"
                            >
                                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-chitta-sky text-chitta-deep">
                                    <Check size={22} strokeWidth={2.2} />
                                </span>
                                <p className="mt-6 max-w-[40ch] text-lg leading-relaxed text-white">{t.contact.success}</p>
                            </div>
                        ) : (
                            <form onSubmit={onSubmit} noValidate data-testid="contact-form" className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                {[
                                    { k: "name", type: "text", auto: "name" },
                                    { k: "email", type: "email", auto: "email" },
                                    { k: "organisation", type: "text", auto: "organization" },
                                ].map((f) => (
                                    <div key={f.k} className={f.k === "organisation" ? "sm:col-span-2" : ""}>
                                        <label htmlFor={`contact-${f.k}`} className="mb-2 block font-display text-xs font-semibold uppercase tracking-[0.18em] text-white/80">
                                            {t.contact.fields[f.k]}
                                        </label>
                                        <input
                                            id={`contact-${f.k}`}
                                            data-testid={`contact-input-${f.k}`}
                                            type={f.type}
                                            autoComplete={f.auto}
                                            value={values[f.k]}
                                            onChange={set(f.k)}
                                            placeholder={t.contact.placeholders[f.k]}
                                            aria-invalid={!!errors[f.k]}
                                            aria-describedby={errors[f.k] ? `contact-${f.k}-error` : undefined}
                                            className={fieldClass(f.k)}
                                        />
                                        {errors[f.k] && (
                                            <p id={`contact-${f.k}-error`} role="alert" data-testid={`contact-error-${f.k}`} className="mt-2 text-sm text-red-300">
                                                {errors[f.k]}
                                            </p>
                                        )}
                                    </div>
                                ))}
                                <div className="sm:col-span-2">
                                    <label htmlFor="contact-message" className="mb-2 block font-display text-xs font-semibold uppercase tracking-[0.18em] text-white/80">
                                        {t.contact.fields.message}
                                    </label>
                                    <textarea
                                        id="contact-message"
                                        data-testid="contact-input-message"
                                        rows={5}
                                        value={values.message}
                                        onChange={set("message")}
                                        placeholder={t.contact.placeholders.message}
                                        aria-invalid={!!errors.message}
                                        aria-describedby={errors.message ? "contact-message-error" : undefined}
                                        className={`${fieldClass("message")} resize-y`}
                                    />
                                    {errors.message && (
                                        <p id="contact-message-error" role="alert" data-testid="contact-error-message" className="mt-2 text-sm text-red-300">
                                            {errors.message}
                                        </p>
                                    )}
                                </div>
                                <div className="sm:col-span-2">
                                    <button
                                        type="submit"
                                        data-testid="contact-submit"
                                        disabled={status === "submitting"}
                                        aria-live="polite"
                                        className="group inline-flex min-h-[52px] items-center gap-3 bg-chitta-sky px-8 font-display text-sm font-semibold lowercase tracking-[0.1em] text-chitta-deep transition-colors duration-200 hover:bg-white disabled:cursor-wait disabled:opacity-70"
                                    >
                                        {status === "submitting" ? t.contact.sending : t.contact.submit}
                                        {status !== "submitting" && (
                                            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                                        )}
                                    </button>
                                </div>
                                <div aria-live="polite" className="sr-only">
                                    {status === "submitting" ? t.contact.sending : ""}
                                </div>
                            </form>
                        )}
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
